import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  FC,
  ReactNode,
  Ref,
  useState,
  useEffect,
} from "react";
import { PlaceholderExtension } from "@remirror/extension-placeholder";
import { LinkExtension } from "@remirror/extension-link";
import { FontSizeExtension } from "@remirror/extension-font-size";
import { HeadingExtension } from "@remirror/extension-heading";
import { FontFamilyExtension } from "@remirror/extension-font-family";
import { TextColorExtension } from "@remirror/extension-text-color";
import { EmojiExtension } from "@remirror/extension-emoji";
import { wysiwygPreset } from "@remirror/preset-wysiwyg";
import {
  EditorComponent,
  Remirror,
  ThemeProvider,
  useRemirror,
  useEditorEvent,
  useHelpers,
  EmojiPopupComponent,
  useRemirrorContext,
} from "@remirror/react";
import { AllStyledComponent } from "@remirror/styles/emotion";
import data from "svgmoji/emoji.json";

import {
  FrameworkProps,
  AnyExtension,
  CreateEditorStateProps,
  EditorView,
  RemirrorJSON,
  PlainExtension,
  InvalidContentHandler,
} from "@remirror/core";
import { WysiwygToolbar } from "../WysiwygToolbar";
import isEqual from "lodash.isequal";

import { ColorOrTransparent } from "../../types";
import { useLinkState } from "../../hooks/useLinkState";
import { CustomImageExtension, LinkTooltipExtension } from "../../extensions";
import { BackgroundColorProps } from "../WysiwygToolbar/Buttons";
import { FloatingLinkToolbar } from "../FloatingLinkToolbar";
import { DEFAULT_AUTO_LINK_REGEX, ALLOWED_TLDS } from "../../constants";
import { useLinkSecurityHandler, LinkSecurityAlert } from "../LinkSecurity";
import "./RichTextEditor.css";

type Label = RemirrorJSON | string | undefined;

export interface RichTextEditorHandle {
  focus: () => void;
  selectAll: () => void;
}

interface ReactEditorProps<T extends AnyExtension>
  extends Pick<CreateEditorStateProps, "stringHandler">,
    Pick<FrameworkProps<T>, "editable"> {
  canvasId: string;
  nodeId: string;
  placeholder: string;
  initialContent: Label;
  onBlur: (json: RemirrorJSON) => void;
  onBackgroundColorChange: BackgroundColorProps["onBackgroundColorChange"];
  backgroundColor: ColorOrTransparent;
  isToolbarVisible: boolean;
  onImageUploadError: () => void;
  isPublic: boolean;
  blockedDomains?: string[];
}

const customTheme = {
  fontFamily: {
    default: "Inter, sans-serif",
  },
  radius: {
    border: "3px",
  },
  space: {
    3: 0,
  },
};

class PreserveMarksOnEnterExtension extends PlainExtension {
  get name() {
    return "preserveMarksOnEnter" as const;
  }

  createPlugin() {
    return {
      appendTransaction: (
        transactions: readonly any[],
        oldState: any,
        newState: any,
      ) => {
        // Check if a paragraph was just split (new block created via Enter)
        const didSplitParagraph = transactions.some((tr: any) => {
          if (!tr.docChanged) return false;

          // Check if the transaction added a new block node (paragraph split)
          const oldBlockCount = oldState.doc.content.childCount;
          const newBlockCount = newState.doc.content.childCount;
          return newBlockCount > oldBlockCount;
        });

        if (!didSplitParagraph) return null;

        // Get the position just before the new paragraph (end of previous paragraph)
        const { $from } = newState.selection;
        const prevParagraphEnd = $from.before();

        if (prevParagraphEnd <= 0) return null;

        // Resolve position at end of previous paragraph to get its marks
        const $prevEnd = newState.doc.resolve(prevParagraphEnd - 1);
        let marks = $prevEnd.marks();

        // If no marks found, try getting from the node before
        if (marks.length === 0 && $prevEnd.nodeBefore) {
          marks = $prevEnd.nodeBefore.marks;
        }

        // Also preserve any stored marks from the old state (toolbar selections)
        const existingStoredMarks = oldState.storedMarks ?? [];
        const mergedMarks = [
          ...existingStoredMarks,
          ...marks.filter(
            (m: any) =>
              !existingStoredMarks.some((sm: any) => sm.type === m.type),
          ),
        ];

        if (mergedMarks.length > 0) {
          return newState.tr.setStoredMarks(mergedMarks);
        }

        return null;
      },
    };
  }
}

class PasteHandlerExtension extends PlainExtension {
  get name() {
    return "pasteHandler" as const;
  }

  createPlugin() {
    return {
      props: {
        handlePaste: (view: EditorView, event: ClipboardEvent) => {
          if (event.clipboardData) {
            const text = event.clipboardData.getData("text/plain").trim();
            const { state, dispatch } = view;
            const isURL = DEFAULT_AUTO_LINK_REGEX.test(text);
            const { from, to } = state.selection;
            const hasTextSelection = from !== to;

            if (dispatch && isURL && hasTextSelection) {
              // If URL is pasted over selected text, turn the selected text into a link
              const linkMark = state.schema.marks.link.create({ href: text });
              const linkText = state.doc.textBetween(from, to);
              const textNode = state.schema.text(linkText, [linkMark]);
              dispatch(state.tr.replaceWith(from, to, textNode));
              return true;
            } else if (dispatch && text && !isURL) {
              // For all other pastes (not a URL), strip formatting by using plain text
              const textNode = state.schema.text(text);
              dispatch(state.tr.replaceSelectionWith(textNode, true));
              return true;
            }
            // If just pasting a URL (no selection), let Remirror handle it for auto-linking
            return false;
          }
          return false; // Let ProseMirror handle the event if clipboardData is not available
        },
      },
    };
  }
}

/**
 * Wraps the toolbars to stop click and mouse-down events from propagating.
 * This is crucial because without it, any click on a toolbar button in active mode
 * would bubble up to the `TextNode`'s `handleNodeClick` handler, incorrectly
 * toggling the editor into text-editing mode.
 */
const PropagationStopper: FC<{ children: ReactNode }> = ({ children }) => (
  <div
    role="presentation"
    onClick={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
  >
    {children}
  </div>
);

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  ReactEditorProps<AnyExtension>
>(function RichTextEditor<T extends AnyExtension>(
  {
    canvasId,
    nodeId,
    placeholder,
    initialContent,
    onBlur,
    isToolbarVisible,
    onBackgroundColorChange,
    backgroundColor,
    editable,
    onImageUploadError,
    isPublic,
    blockedDomains = [],
    ...rest
  }: ReactEditorProps<T>,
  ref: Ref<RichTextEditorHandle>,
) {
  // Link security handler with state management
  const {
    alertType,
    handleLinkClick,
    handleConfirmationCancel,
    handleConfirmationContinue,
    handleBlockedDismiss,
  } = useLinkSecurityHandler({ isPublic, blockedDomains });

  // Alert position state
  const [alertPosition, setAlertPosition] = useState({ top: 0, left: 0 });

  const extensions = useCallback(() => {
    const linkExtension = new LinkExtension({
      autoLink: true,
      defaultTarget: "_blank",
      selectTextOnClick: true,
      defaultProtocol: "https:",
      autoLinkAllowedTLDs: ALLOWED_TLDS,
      openLinkOnClick: false,
    });

    return [
      new PlaceholderExtension({ placeholder }),
      new FontSizeExtension({ defaultSize: "16", unit: "px" }),
      new HeadingExtension({}),
      new TextColorExtension({}),
      new FontFamilyExtension(),
      // @ts-ignore
      new EmojiExtension({ data, moji: "noto" }),
      linkExtension,
      new PreserveMarksOnEnterExtension(),
      new PasteHandlerExtension(),
      new CustomImageExtension({
        canvasId,
        onImageUploadError,
        enableResizing: true,
      }),
      new LinkTooltipExtension(),
      ...wysiwygPreset(), // This must go last to ensure our custom extensions are not overwritten by the preset
    ];
  }, [placeholder, canvasId, onImageUploadError]);

  // DOM event listener for link clicks (needed for positioning)
  const editorContainerRef = useRef<HTMLDivElement>(null);

  // Note: We use DOM event listener instead of Remirror handler
  // because we need access to the actual anchor element for positioning
  useEffect(() => {
    // Only intercept link clicks on public canvases for security
    if (!isPublic) return;

    const container = editorContainerRef.current;
    if (!container) return;

    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement;

      if (anchor && anchor.getAttribute("href")) {
        event.preventDefault();
        event.stopPropagation();

        // Set alert position near the clicked anchor
        const anchorRect = anchor.getBoundingClientRect();

        const alertPosition = {
          top: anchorRect.bottom + 9,
          left: anchorRect.right - 37,
        };

        setAlertPosition(alertPosition);
        handleLinkClick(anchor.href);
      }
    };

    // Use capture phase to intercept before other handlers
    container.addEventListener("click", handleClick, true);
    return () => {
      container.removeEventListener("click", handleClick, true);
    };
  }, [handleLinkClick, isPublic]);

  const focusRef = useRef<() => void>(() => {});
  const selectAllRef = useRef<() => void>(() => {});

  useImperativeHandle(ref, () => ({
    focus: () => {
      focusRef.current();
    },
    selectAll: () => {
      selectAllRef.current();
    },
  }));

  const onError: InvalidContentHandler = useCallback(
    ({ json, invalidContent, transformers }) => {
      // Automatically remove all invalid nodes and marks.
      return transformers.remove(json, invalidContent);
    },
    [],
  );

  const { manager, state } = useRemirror({
    extensions,
    stringHandler: "html",
    content: initialContent,
    onError,
  });

  // Helper component inside Remirror context
  function RemirrorImperativeHandleSetter() {
    const { commands } = useRemirrorContext();

    selectAllRef.current = () => {
      commands.selectAll();
    };

    focusRef.current = () => {
      commands.focus("end");
    };

    return null;
  }

  return (
    <div ref={editorContainerRef} className="rich-text-editor-wrapper">
      <AllStyledComponent>
        <ThemeProvider theme={customTheme}>
          <Remirror
            manager={manager}
            initialContent={state}
            editable={editable}
            classNames={["rich-text-editor"]}
            {...rest}
          >
            {isToolbarVisible && (
              <PropagationStopper>
                <WysiwygToolbar
                  nodeId={nodeId}
                  backgroundColor={backgroundColor}
                  onBackgroundColorChange={onBackgroundColorChange}
                  onBlur={onBlur}
                />
                <FloatingLinkToolbar nodeId={nodeId} />
              </PropagationStopper>
            )}
            <Editor
              nodeId={nodeId}
              onBlur={onBlur}
              initialContent={initialContent}
            />
            <RemirrorImperativeHandleSetter />
            <EmojiPopupComponent />
          </Remirror>
          <LinkSecurityAlert
            alertType={alertType}
            alertPosition={alertPosition}
            onConfirmationCancel={handleConfirmationCancel}
            onConfirmationContinue={handleConfirmationContinue}
            onBlockedDismiss={handleBlockedDismiss}
          />
        </ThemeProvider>
      </AllStyledComponent>
    </div>
  );
});

function Editor({
  onBlur,
  initialContent,
  nodeId,
}: {
  onBlur: (json: RemirrorJSON) => void;
  initialContent: Label;
  nodeId: string;
}) {
  const { getJSON } = useHelpers();
  const previousJSON = useRef<Label>(initialContent);

  /* useLinkState helps reduce the flash of the LinkDialog when:
   * 1. LinkDialog is open
   * 2. Editor loses focus (onBlur)
   * 3. User clicks back into the editor
   * Without this line, you'll see a brief flash of the LinkDialog before it disappears
   */
  useLinkState(nodeId);

  const handleOnBlur = useCallback(() => {
    const currentJSON = getJSON();
    const hasChanges = !isEqual(previousJSON.current, currentJSON);

    if (hasChanges) {
      onBlur(currentJSON);
      previousJSON.current = currentJSON;
    }
  }, [getJSON, onBlur]);

  useEditorEvent("blur", handleOnBlur);

  return <EditorComponent />;
}
